import { NgClass } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DialogService } from '@ngneat/dialog';
import { ACTIVE_AGENTS } from '@shared/utils';
import { filter } from 'rxjs';
import { UiKitBaseComponent } from '../../../core/ui-kit/components/ui-kit-base/ui-kit-base.component';
import { RoleDirective } from '../../../features/authentication/directives/role.directive';
import { Role } from '../../../features/authentication/enums/role.enum';
import { AuthenticationService } from '../../../features/authentication/services/authentication.service';
import { UIFacade } from '../../data/data-access-ui';
import { Timeout } from '../../decorators/timeout.decorator';
import { StyleService } from '../../services/style.service';
import { NewChatDialogComponent } from '../new-chat-dialog/new-chat-dialog.component';
import { UserProfileComponent } from '../user-profile/user-profile.component';
import { DESKTOP_MENU } from './utils/desktop-menu';
import { MOBILE_MENU } from './utils/mobile-menu';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [NgClass, RouterLink, RouterLinkActive, TranslocoPipe, UserProfileComponent, RoleDirective],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent extends UiKitBaseComponent {
  private readonly uiFacade = inject(UIFacade);
  private readonly dialogService = inject(DialogService);
  private readonly styleService = inject(StyleService);
  private readonly authService = inject(AuthenticationService);

  protected readonly Role = Role;

  isCollapsed = signal(false);

  desktopItems = computed(() => {
    const chatItemKey = 'op__chat';
    const isChatVisible = this.isChatVisible();
    return DESKTOP_MENU.map((item) => {
      if (item.pageId === chatItemKey && !isChatVisible) {
        return { ...item, visible: false };
      }

      return { ...item, visible: true };
    });
  });
  mobileItems = computed(() => {
    const chatItemKey = 'op__chat';
    const isChatVisible = this.isChatVisible();
    return MOBILE_MENU.map((item) => {
      if (item.pageId === chatItemKey && !isChatVisible) {
        return { ...item, visible: false };
      }

      return { ...item, visible: true };
    });
  });

  agents = computed(() => this.allAgents().filter((agent) => this.user()?.verticals.includes(agent.id)));

  isChatVisible = toSignal(this.uiFacade.chatVisible$);

  private allAgents = signal(ACTIVE_AGENTS);
  private user = toSignal(this.authService.user$.pipe(filter(Boolean)));

  toggleSidebar(): void {
    this.isCollapsed.update((collapsed) => !collapsed);

    this.uiFacade.setSidebarCollapsed(this.isCollapsed());

    this.updateSidebarWidths();
  }

  openNewChatDialog() {
    this.dialogService.open(NewChatDialogComponent, {
      width: '42rem',
      windowClass: 'no-background',
      enableClose: false,
      data: {},
    });
  }

  @Timeout(300)
  private updateSidebarWidths() {
    this.styleService.setSidebarWidths();
  }
}
